import i18n, { t } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import OCCOBackend from './i18nOCCOBackend.js';
import { getDefaultLanguageSettings, normalizeLanguageSettings } from './languageSettings.js';

/*
 * @param {Object} params
 * @param {number} params.orgId
 * @param {string} params.eventCode
 * @param {'webapp'|'universal-kiosk'} params.projectName
 * @param {boolean} [params.autoApplyDataLoc=true]
 * @param {{ availableLanguages: string[], defaultLanguage: string }} [params.languageSettings]
 * @param {string} [params.initialLanguage]
 * @param {Object} [params.detection]
 */
export async function initLoc({
  orgId,
  eventCode,
  projectName,
  autoApplyDataLoc = true,
  languageSettings = getDefaultLanguageSettings(),
  initialLanguage,
  detection,
} = {}) {
  const normalizedLanguageSettings = normalizeLanguageSettings(languageSettings);
  const supportedLngs = Array.from(new Set([...normalizedLanguageSettings.availableLanguages, 'en']));
  const lng = normalizedLanguageSettings.availableLanguages.includes(initialLanguage) ? initialLanguage : undefined;

  await i18n
    .use(LanguageDetector)
    .use(OCCOBackend)
    .init({
      fallbackLng: 'en',
      ...(lng ? { lng } : {}),
      ...(detection ? { detection } : {}),
      load: 'languageOnly',
      debug: true,
      supportedLngs,
      backend: {
        cdn: process.env.LOCALIZATION_CDN,
        orgId,
        eventCode,
        projectName,
      },
      ns: ['common'],
      defaultNS: 'common',
    });

  if (autoApplyDataLoc) {
    applyDataLocAttributes();
  }
}

export function applyDataLocAttributes() {
  const root = typeof document !== 'undefined' ? document : null;
  if (!root || typeof root.querySelectorAll !== 'function') {
    return;
  }

  const applyFromAttribute = (selector, attributeName, applyValue) => {
    root.querySelectorAll(selector).forEach((element) => {
      const key = element.getAttribute(attributeName);
      if (!key) return;

      const translated = t(key);
      if (translated === undefined || translated === null) return;

      applyValue(element, translated);
    });
  };

  applyFromAttribute('[data-loc-text]', 'data-loc-text', (element, value) => {
    element.textContent = value;
  });

  applyFromAttribute('[data-loc-placeholder]', 'data-loc-placeholder', (element, value) => {
    element.setAttribute('placeholder', value);
  });

  applyFromAttribute('[data-loc-title]', 'data-loc-title', (element, value) => {
    element.setAttribute('title', value);
  });

  applyFromAttribute('[data-loc-aria-label]', 'data-loc-aria-label', (element, value) => {
    element.setAttribute('aria-label', value);
  });
}

export { t };

export async function changeLanguage(lng) {
  return i18n.changeLanguage(lng);
}

export function getCurrentLanguage() {
  return i18n.language;
}
